import { AlertCircle, FileSpreadsheet, Upload } from "lucide-react";
import React from "react";

const ExcelImportModal = ({
  excelData,
  setExcelData,
  fileInputRef,
  handleFileChange,
  handleImport,
  loadingExcel,
  excelError,
  excelFileName,
}) => {
  return (
    <div className="p-6">
      {/* Step 1: File Upload Area */}
      {excelData.length === 0 ? (
        <div
          className="relative group border-2 border-dashed border-slate-300 rounded-xl p-12 transition-all hover:border-blue-400 hover:bg-blue-50/50 text-center cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            className="hidden"
            ref={fileInputRef}
            accept=".xlsx,.xls"
            onChange={handleFileChange} // Move your logic to a named function for cleanliness
          />
          <div className="flex flex-col items-center">
            <div className="p-4 bg-blue-100 text-blue-600 rounded-full mb-4 group-hover:scale-110 transition-transform">
              <Upload size={32} />
            </div>
            <h4 className="text-lg font-semibold text-slate-800">
              Upload your spreadsheet
            </h4>
            <p className="text-sm text-slate-500 mt-1">
              Drag and drop or{" "}
              <span className="text-blue-600 font-medium">browse files</span>
            </p>
            <div className="mt-6 flex gap-3 text-xs font-medium text-slate-400">
              <span className="px-2 py-1 bg-slate-100 rounded">.XLSX</span>
              <span className="px-2 py-1 bg-slate-100 rounded">.XLS</span>
            </div>
          </div>
        </div>
      ) : (
        /* Step 2: Data Preview State */
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 text-green-700 rounded-md">
                <FileSpreadsheet size={20} />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-700">
                  {excelFileName}
                </div>
                <div className="text-xs text-slate-500">
                  {excelData.length} records found
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setExcelData([]);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg text-sm font-medium transition-colors"
              >
                Change File
              </button>
              <button
                onClick={handleImport}
                disabled={loadingExcel}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-all"
              >
                {loadingExcel ? "Importing..." : <>Import All Records</>}
              </button>
            </div>
          </div>

          {excelError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg border border-red-100 text-sm">
              <AlertCircle size={16} /> {excelError}
            </div>
          )}

          {/* Data Table Preview */}
          <div className="border rounded-xl overflow-hidden shadow-sm bg-white">
            <div className="overflow-x-auto max-h-[40vh]">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    {Object.keys(excelData[0] || {})
                      .slice(0, 6)
                      .map((h) => (
                        <th
                          key={h}
                          className="p-3 text-xs font-bold text-slate-500 uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {excelData.slice(0, 10).map((row, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50/50 transition-colors"
                    >
                      {Object.keys(excelData[0] || {})
                        .slice(0, 6)
                        .map((h) => {
                          const isDescription = h
                            .toLowerCase()
                            .includes("description");

                          return (
                            <td
                              key={h}
                              className={`p-3 text-sm text-slate-600 ${
                                isDescription
                                  ? "min-w-[250px] max-w-[400px] whitespace-normal break-words leading-relaxed"
                                  : "whitespace-nowrap"
                              }`}
                            >
                              {row[h]?.toString() || (
                                <span className="text-slate-300 italic">
                                  empty
                                </span>
                              )}
                            </td>
                          );
                        })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {excelData.length > 10 && (
              <div className="p-3 bg-slate-50 border-t text-center text-xs text-slate-400">
                Showing first 10 rows out of {excelData.length}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer Helper */}
      <div className="mt-6 p-4 rounded-lg bg-amber-50 border border-amber-100">
        <h5 className="text-xs font-bold text-amber-800 uppercase mb-1">
          Expected Format
        </h5>
        <p className="text-xs text-amber-700 leading-relaxed">
          Ensure your columns are titled:{" "}
          <code className="bg-amber-100 px-1 rounded">code</code>,
          <code className="bg-amber-100 px-1 rounded">description</code>,
          <code className="bg-amber-100 px-1 rounded">qty</code>, and
          <code className="bg-amber-100 px-1 rounded">unit price</code>.
        </p>
      </div>
    </div>
  );
};

export default ExcelImportModal;
